import { getCurrentUser } from "@aws-amplify/auth";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserService from "../../../api/UserService";
import LoginPage from "../../../pages/Login/LoginPage";
import { publicPaths } from "../../../routes";
import LayoutConfigProvider from "../../context/LayoutConfigProvider";
import { useLoading } from "../../context/LoadingProvider";
import PopUpProvider from "../../context/PopUpProvider";
import SnackbarProvider from "../../context/SnackbarProvider";
import { CognitoUser } from "../../models/Models";
import LoggedInView from "../LoggedInView";

export default function Layout() {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const { setIsLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleFederatedUser = async () => {
      const params = new URLSearchParams(location.search);
      if (params.get("federated") === "true") {
        // Immediately remove the parameter from URL to prevent re-execution
        params.delete("code");
        params.delete("state");
        params.delete("federated");
        
        // Update URL without triggering navigation
        const newUrl = location.pathname + (params.toString() ? `?${params.toString()}` : '');
        window.history.replaceState({}, '', newUrl);
        
        setIsLoading(true);

        try {
          await UserService.handleFederatedUser();
          const savedPath = localStorage.getItem("navigateAfterLogin");

          if (savedPath) {
            localStorage.removeItem("navigateAfterLogin");
            navigate(savedPath, { replace: true });
          }
        } catch (error) {
          console.error("Failed to handle federated user:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    const getUser = async () => {
      try {
        const user = await getCurrentUser();
        setUser(user);

        const savedPath = localStorage.getItem("navigateAfterLogin");
        if (savedPath && location.pathname === "/") {
          localStorage.removeItem("navigateAfterLogin");
          navigate(savedPath, { replace: true });
        } else if (location.pathname === "/forgot-password") {
          navigate("/", { replace: true });
        } else if (location.pathname === "/" || location.pathname === "") {
          // Navigate to Alqemi dashboard by default after login
          navigate("/alqemi", { replace: true });
        }
      } catch (error) {
        localStorage.clear();
        if (!publicPaths.includes(location.pathname)) {
          navigate(
            `/?navigateAfterLogin=${encodeURIComponent(location.pathname)}`,
            {
              replace: true,
            }
          );
        }
      }
    };

    const init = async () => {
      await handleFederatedUser();
      await getUser();
    };

    init();
  }, [location.search, navigate, location.pathname]);

  return (
    <PopUpProvider>
      <SnackbarProvider>
        <LayoutConfigProvider>
          {user ? <LoggedInView /> : <LoginPage />}
        </LayoutConfigProvider>
      </SnackbarProvider>
    </PopUpProvider>
  );
}
